import { useState } from 'react';

interface Phone {
  label: string;
  number: string;
}

interface ContactData {
  address: string;
  city: string;
  state: string;
  zip: string;
  phones: Phone[];
  email: string;
  business_hours: string;
  service_area: string;
  map_query: string;
}

interface ContactEditorProps {
  data: ContactData;
  onChange: (data: ContactData) => void;
}

export default function ContactEditor({ data, onChange }: ContactEditorProps) {
  const [editingPhoneIndex, setEditingPhoneIndex] = useState<number | null>(null);

  const handleChange = (field: keyof ContactData, value: string) => {
    onChange({
      ...data,
      [field]: value,
    });
  };

  const handleAddPhone = () => {
    onChange({
      ...data,
      phones: [...data.phones, { label: '', number: '' }],
    });
    setEditingPhoneIndex(data.phones.length);
  };

  const handleUpdatePhone = (index: number, field: 'label' | 'number', value: string) => {
    const newPhones = [...data.phones];
    newPhones[index][field] = value;
    onChange({
      ...data,
      phones: newPhones,
    });
  };

  const handleDeletePhone = (index: number) => {
    if (confirm('¿Eliminar este teléfono?')) {
      onChange({
        ...data,
        phones: data.phones.filter((_, i) => i !== index),
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b pb-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <i className="ri-phone-line text-blue-600"></i>
          Información de Contacto
        </h2>
        <p className="text-gray-600 mt-1">Dirección, teléfonos, horarios y mapa</p>
      </div>

      <div className="space-y-6">
        {/* Dirección */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-map-pin-line text-blue-600"></i>
            Dirección
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dirección Completa
              </label>
              <input
                type="text"
                value={data.address}
                onChange={(e) => handleChange('address', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="655 Sebastopol Rd"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ciudad</label>
              <input
                type="text"
                value={data.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Santa Rosa"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
              <input
                type="text"
                value={data.state}
                onChange={(e) => handleChange('state', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="CA"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Código Postal
              </label>
              <input
                type="text"
                value={data.zip}
                onChange={(e) => handleChange('zip', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="95407"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Área de Servicio
              </label>
              <input
                type="text"
                value={data.service_area}
                onChange={(e) => handleChange('service_area', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Santa Rosa, CA"
              />
            </div>
          </div>
        </div>

        {/* Teléfonos */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <i className="ri-phone-line text-blue-600"></i>
              Teléfonos
            </h3>
            <button
              onClick={handleAddPhone}
              className="bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition-colors text-sm whitespace-nowrap"
            >
              <i className="ri-add-line mr-1"></i>
              Agregar
            </button>
          </div>
          <div className="space-y-3">
            {data.phones.map((phone, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={phone.label}
                  onChange={(e) => handleUpdatePhone(index, 'label', e.target.value)}
                  className="w-1/3 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Etiqueta (Oficina, WhatsApp)"
                />
                <input
                  type="text"
                  value={phone.number}
                  onChange={(e) => handleUpdatePhone(index, 'number', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="(707) 369-9830"
                />
                <button
                  onClick={() => handleDeletePhone(index)}
                  className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Email y Horarios */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email de Contacto
            </label>
            <input
              type="email"
              value={data.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="mkt@axloweb.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Horario de Atención
            </label>
            <textarea
              value={data.business_hours}
              onChange={(e) => handleChange('business_hours', e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Lunes a Viernes: 9:00 AM - 6:00 PM"
            />
          </div>
        </div>

        {/* Mapa */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-map-line text-blue-600"></i>
            Configuración del Mapa
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Consulta para Google Maps
            </label>
            <input
              type="text"
              value={data.map_query}
              onChange={(e) => handleChange('map_query', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="655 Sebastopol Rd, Santa Rosa, CA 95407"
            />
            <p className="text-sm text-gray-500 mt-2">
              Esta dirección se usará para generar el mapa de Google en la sección de contacto
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
